const managedVideos = new WeakSet();
const observedVideos = new Set();
const initializedDialogs = new WeakSet();

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const detailQueryParameter = 'look';

const videoObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const video = entry.target;
            if (!(video instanceof HTMLVideoElement)) continue;

            if (entry.isIntersecting && !document.hidden && !reduceMotion.matches && !getOpenDetail()) {
              video.play().catch(() => {});
            } else {
              video.pause();
            }
          }
        },
        { rootMargin: '160px 0px', threshold: 0.08 }
      )
    : null;

function getDetail() {
  return document.querySelector('[data-lookbook-detail]');
}

function getOpenDetail() {
  return document.querySelector('[data-lookbook-detail][open]');
}

function getCards() {
  return Array.from(document.querySelectorAll('[data-lookbook-card]'));
}

function registerLookbookVideos(root = document) {
  const videos = root.querySelectorAll?.('video.lookbook-card__asset') ?? [];

  for (const video of videos) {
    if (video.closest('[data-lookbook-detail]') || managedVideos.has(video)) continue;
    managedVideos.add(video);
    observedVideos.add(video);

    video.muted = true;
    video.playsInline = true;

    if (videoObserver) {
      videoObserver.observe(video);
    } else if (!reduceMotion.matches) {
      video.play().catch(() => {});
    }
  }
}

function pauseGridVideos() {
  for (const video of observedVideos) video.pause();
}

function resumeGridVideos() {
  if (!videoObserver || document.hidden || reduceMotion.matches) return;

  for (const video of observedVideos) {
    const rect = video.getBoundingClientRect();
    if (rect.bottom >= -160 && rect.top <= window.innerHeight + 160) {
      video.play().catch(() => {});
    }
  }
}

function updatePlayback() {
  for (const video of observedVideos) {
    if (!video.isConnected) {
      observedVideos.delete(video);
      videoObserver?.unobserve(video);
      continue;
    }

    if (document.hidden || reduceMotion.matches || getOpenDetail()) video.pause();
  }

  const detailVideo = getOpenDetail()?.querySelector('video');
  if (detailVideo instanceof HTMLVideoElement) {
    if (document.hidden || reduceMotion.matches) {
      detailVideo.pause();
    } else {
      detailVideo.play().catch(() => {});
    }
  }
}

function prepareDialog(dialog) {
  if (!(dialog instanceof HTMLDialogElement) || initializedDialogs.has(dialog)) return;
  initializedDialogs.add(dialog);

  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    requestDetailClose();
  });
}

function openDetail(card, { updateHistory = true } = {}) {
  const dialog = getDetail();
  const cards = getCards();
  const cardIndex = cards.indexOf(card);
  const sourceAsset = card?.querySelector('.lookbook-card__asset');
  const mediaContainer = dialog?.querySelector('[data-lookbook-detail-media]');
  const counter = dialog?.querySelector('[data-lookbook-counter]');

  if (!(dialog instanceof HTMLDialogElement) || cardIndex < 0 || !sourceAsset || !mediaContainer || !counter) return;

  prepareDialog(dialog);

  const clonedAsset = sourceAsset.cloneNode(true);
  mediaContainer.replaceChildren(clonedAsset);
  mediaContainer.style.setProperty('--lookbook-media-scale', card.style.getPropertyValue('--lookbook-media-scale'));
  mediaContainer.style.setProperty('--lookbook-object-fit', card.style.getPropertyValue('--lookbook-object-fit'));
  mediaContainer.style.setProperty(
    '--lookbook-object-position-x',
    card.style.getPropertyValue('--lookbook-object-position-x')
  );
  mediaContainer.style.setProperty(
    '--lookbook-object-position-y',
    card.style.getPropertyValue('--lookbook-object-position-y')
  );

  counter.textContent = `${cardIndex + 1} / ${cards.length}`;
  dialog.dataset.activeLook = card.dataset.lookId ?? '';
  dialog.setAttribute('aria-label', card.getAttribute('aria-label') ?? `Look ${cardIndex + 1}`);

  if (!dialog.open) dialog.showModal();
  window.requestAnimationFrame(() => dialog.classList.add('is-visible'));
  pauseGridVideos();

  if (clonedAsset instanceof HTMLVideoElement && !reduceMotion.matches) {
    clonedAsset.muted = true;
    clonedAsset.playsInline = true;
    clonedAsset.preload = 'auto';
    clonedAsset.play().catch(() => {});
  }

  if (updateHistory) {
    const url = new URL(window.location.href);
    url.searchParams.set(detailQueryParameter, card.dataset.lookId ?? String(cardIndex + 1));
    window.history.pushState({ lookbookDetail: true }, '', url);
  }
}

function closeDetailImmediately() {
  const dialog = getOpenDetail();
  if (!(dialog instanceof HTMLDialogElement)) return;

  dialog.classList.remove('is-visible');
  dialog.querySelector('video')?.pause();
  dialog.close();
  dialog.querySelector('[data-lookbook-detail-media]')?.replaceChildren();
  resumeGridVideos();
}

function requestDetailClose() {
  const dialog = getOpenDetail();
  if (!(dialog instanceof HTMLDialogElement)) return;

  dialog.classList.remove('is-visible');

  if (window.history.state?.lookbookDetail) {
    window.history.back();
    return;
  }

  const url = new URL(window.location.href);
  url.searchParams.delete(detailQueryParameter);
  window.history.replaceState(window.history.state, '', url);
  window.setTimeout(closeDetailImmediately, reduceMotion.matches ? 0 : 280);
}

function syncDetailFromUrl() {
  const lookId = new URL(window.location.href).searchParams.get(detailQueryParameter);
  if (!lookId) {
    closeDetailImmediately();
    return;
  }

  const card = getCards().find((candidate) => candidate.dataset.lookId === lookId);
  if (card) openDetail(card, { updateHistory: false });
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const card = target.closest('[data-lookbook-card]');
  if (card) {
    openDetail(card);
    return;
  }

  if (target.closest('[data-lookbook-close]') || target.closest('[data-lookbook-view-all]')) {
    event.preventDefault();
    requestDetailClose();
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  const target = event.target;
  if (!(target instanceof Element)) return;

  const card = target.closest('[data-lookbook-card]');
  if (!card) return;
  event.preventDefault();
  openDetail(card);
});

registerLookbookVideos();
prepareDialog(getDetail());
syncDetailFromUrl();

document.addEventListener('visibilitychange', updatePlayback);
reduceMotion.addEventListener?.('change', updatePlayback);
window.addEventListener('popstate', syncDetailFromUrl);

document.addEventListener('shopify:section:load', (event) => {
  registerLookbookVideos(event.target);
  prepareDialog(getDetail());
  syncDetailFromUrl();
});
